import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { agentsApi, agentRunsApi } from "../services/api";
import { SOURCE_TRUTH, classifySourceTruth, isCurrentTruth } from "../components/sourceTruth";
import SourceTruthBadge from "../components/SourceTruthBadge";
import { useOnlineStatus } from "../components/useLiveState";

// ── Command Center — CC015: Live Data Adapter ────────────────────────────────
// Interactive state machine: Core + per-agent states changeable.
// Agents/runs/stats come from existing read-only GET APIs (polled); delegations, knowledge
// accesses, recent actions and the Core state are static samples. See DISPLAY_SOURCE_TRUTH below.

const BUILD_VERSION = "CC015";

// ── Source truth (UDI-SOURCE-TRUTH-AUDIT-001) ─────────────────────────────────
// Every data surface is classified so mock/fallback values are never read as
// live production truth.
//   LIVE        pushed in real time (no such surface exists yet)
//   POLLING     fetched from an existing GET API on an interval
//   CACHED      last successful poll, now failing — may be stale
//   FALLBACK    API failed and no earlier data exists — static sample shown
//   MOCK        static sample by design (no live source exists / API returned nothing)
//   UNAVAILABLE no data can be shown
const DISPLAY_SOURCE_TRUTH = {
  LIVE:        { label: "חי",         color: "#4ade80", hint: "נתונים בזמן אמת" },
  POLLING:     { label: "סקירה 15ש׳", color: "#38bdf8", hint: "נתונים אמיתיים מ-API קיים, מתרעננים כל 15 שניות" },
  CACHED:      { label: "שמור",       color: "#fbbf24", hint: "הסקירה האחרונה נכשלה — מוצג המידע האחרון שהתקבל, ייתכן שאינו עדכני" },
  FALLBACK:    { label: "גיבוי",      color: "#fb923c", hint: "ה-API אינו זמין — מוצגים נתוני הדגמה שאינם משקפים את המערכת" },
  MOCK:        { label: "הדגמה",      color: "#c084fc", hint: "נתוני הדגמה סטטיים — אינם משקפים את המערכת" },
  UNAVAILABLE: { label: "לא זמין",    color: "#94a3b8", hint: "אין מקור נתונים לפריט זה" },
};

// The header sentence is about the agent source only; agent *states* additionally require the run
// feed (see runsUsableForState). Wording is chosen by evidence, never by the fact that a poller exists.
const AGENT_TRUTH_TEXT = {
  [SOURCE_TRUTH.LIVE]: "נתונים בזמן אמת",
  [SOURCE_TRUTH.POLLING]: "נתונים אמיתיים",
  [SOURCE_TRUTH.CACHED]: "נתונים שמורים",
  [SOURCE_TRUTH.STALE]: "הרענון נכשל — מוצג מידע שמור",
  [SOURCE_TRUTH.UNAVAILABLE]: "נתוני הדגמה — המקור אינו זמין",
  [SOURCE_TRUTH.DENIED]: "אין הרשאה למקור",
  [SOURCE_TRUTH.LOADING]: "טוען נתונים…",
  [SOURCE_TRUTH.UNKNOWN]: "מצב המקור לא ידוע",
};

function SourceBadge({ kind, className = "" }) {
  const s = DISPLAY_SOURCE_TRUTH[kind] ?? DISPLAY_SOURCE_TRUTH.UNAVAILABLE;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[9px] rounded border px-1 leading-4 ${className}`}
      style={{ color: s.color, borderColor: s.color + "55", background: s.color + "14" }}
      title={s.hint}
      data-source={kind}
    >
      {s.label}
    </span>
  );
}

// Classify the displayed payload (not the feed): a sample is shown until a non-empty live read
// exists, so that is labelled MOCK/FALLBACK even while the underlying source is still loading.
// "POLLING" now requires both a successful live payload AND an online browser; a held payload
// while offline or after a failed refresh is CACHED, never a live claim.
const NO_DATA = [];

function classifyQuerySource(query, hasUsableData, online) {
  if (query?.isError) return hasUsableData ? "CACHED" : "FALLBACK";
  if (online === false) return hasUsableData ? "CACHED" : "UNAVAILABLE";
  return hasUsableData ? "POLLING" : "MOCK";
}

// ── State machine definition ──────────────────────────────────────────────────
// Valid agent state transitions (used to build cycling order)
const AGENT_STATE_ORDER = ["idle", "planning", "working", "waiting", "approval_required", "completed", "failed"];

const CORE_STATE_ORDER = ["idle", "active", "working", "approval_required", "failed"];

// ── Core state palette ────────────────────────────────────────────────────────
const CORE_STATE = {
  idle:              { label: "המתנה",      color: "#7c3aed", glow: "rgba(124,58,237,0.35)",  ring: "#6d28d9", outer: "#4c1d95" },
  active:            { label: "פעיל",        color: "#2563eb", glow: "rgba(37,99,235,0.35)",   ring: "#1d4ed8", outer: "#1e3a8a" },
  approval_required: { label: "ממתין לאישור", color: "#d97706", glow: "rgba(217,119,6,0.35)",   ring: "#b45309", outer: "#78350f" },
  working:           { label: "בעבודה",       color: "#059669", glow: "rgba(5,150,105,0.35)",   ring: "#047857", outer: "#064e3b" },
  failed:            { label: "כשל",          color: "#dc2626", glow: "rgba(220,38,38,0.35)",   ring: "#b91c1c", outer: "#7f1d1d" },
};

// ── Agent identity registry — type → visual config ────────────────────────────
const AGENT_IDENTITY = {
  "IT Support":    { accent: "#0d9488", bg: "#0a1f1e", dimBg: "#071514", icon: "🎧", shape: "circle",  roleHe: "תמיכה"    },
  "IT Expert":     { accent: "#3b82f6", bg: "#0b1630", dimBg: "#07101f", icon: "🧠", shape: "hex",     roleHe: "מומחה"    },
  "Security":      { accent: "#ef4444", bg: "#200a0a", dimBg: "#160606", icon: "🛡",  shape: "diamond", roleHe: "אבטחה"    },
  "Discovery":     { accent: "#f59e0b", bg: "#1f1300", dimBg: "#140c00", icon: "🔍", shape: "circle",  roleHe: "גילוי"    },
  "Documentation": { accent: "#64748b", bg: "#111827", dimBg: "#0c1117", icon: "📄", shape: "square",  roleHe: "תיעוד"    },
  "Research":      { accent: "#a78bfa", bg: "#180f2e", dimBg: "#10091f", icon: "🔬", shape: "hex",     roleHe: "מחקר"     },
  "Finance":       { accent: "#10b981", bg: "#082217", dimBg: "#051510", icon: "💰", shape: "circle",  roleHe: "פיננסים"  },
  "Developer":     { accent: "#f97316", bg: "#1f0e00", dimBg: "#130900", icon: "💻", shape: "square",  roleHe: "פיתוח"    },
};

// A live agent whose name has no registered identity gets a neutral one. It must
// not borrow "IT Support" — that would label an unknown agent with a role it may
// not have (UDI-CONT-SOURCE-TRUTH-AUDIT-001).
const NEUTRAL_IDENTITY = { accent: "#94a3b8", bg: "#111827", dimBg: "#0c1117", icon: "🤖", shape: "circle", roleHe: null };
const identityFor = (name) => AGENT_IDENTITY[name] ?? NEUTRAL_IDENTITY;

// "tier_1".."tier_3" -> 1..3. Anything else is unknown (null), never defaulted to Tier 1.
const tierNumber = (tier) => {
  const m = /^tier_(\d+)$/.exec(tier ?? "");
  return m ? Number(m[1]) : null;
};

// ── Agent state rendering ─────────────────────────────────────────────────────
const AGENT_STATE = {
  idle:              { dot: "#475569", label: "#64748b",  text: "idle",      ringOpacity: 0,    pulsing: false },
  planning:          { dot: "#818cf8", label: "#a5b4fc",  text: "מתכנן",    ringOpacity: 0.5,  pulsing: false },
  working:           { dot: "#4ade80", label: "#86efac",  text: "בעבודה",   ringOpacity: 0.65, pulsing: true  },
  waiting:           { dot: "#a8a29e", label: "#d6d3d1",  text: "ממתין",    ringOpacity: 0.3,  pulsing: false },
  unknown:           { dot: "#334155", label: "#64748b",  text: "לא זמין",  ringOpacity: 0,    pulsing: false },
  approval_required: { dot: "#fbbf24", label: "#fde68a",  text: "אישור ⚠", ringOpacity: 1,    pulsing: true  },
  completed:         { dot: "#34d399", label: "#6ee7b7",  text: "הושלם",    ringOpacity: 0.5,  pulsing: false },
  failed:            { dot: "#f87171", label: "#fca5a5",  text: "כשל",       ringOpacity: 0.9,  pulsing: true  },
};

const STATE_RING_COLOR = {
  idle:              "transparent",
  planning:          "#4338ca",
  working:           "#15803d",
  waiting:           "#78716c",
  approval_required: "#d97706",
  completed:         "#059669",
  failed:            "#dc2626",
};

// ── Mock agents ───────────────────────────────────────────────────────────────
const MOCK_AGENTS = [
  {
    id: "a1", name: "IT Support", nameHe: "תמיכה", state: "working",
    task: "ניתוח קריאה #1042", model: "claude-haiku", runs: 17, tier: 2,
    ticketId: "1042", elapsedSec: 142, tokens: 14820, costUsd: 0.0059,
    tools: ["analyze_ticket", "search_knowledge", "add_comment"],
    recentActions: [
      { label: "ניתח קריאה #1042", time: "לפני 2 דק", type: "analyze" },
      { label: "חיפש מאמר ידע",     time: "לפני 3 דק", type: "search"  },
      { label: "אשר הערה פנימית",   time: "לפני 7 דק", type: "action"  },
    ],
  },
  {
    id: "a2", name: "IT Expert", nameHe: "מומחה", state: "approval_required",
    task: "המלצה לסגירה #1038", model: "claude-sonnet", runs: 8, tier: 2,
    ticketId: "1038", elapsedSec: 310, tokens: 32400, costUsd: 0.0324,
    tools: ["analyze_ticket", "close_ticket", "send_customer_message"],
    recentActions: [
      { label: "הציע סגירת קריאה", time: "לפני 1 דק", type: "action"  },
      { label: "ניתח קריאה #1038", time: "לפני 6 דק", type: "analyze" },
    ],
  },
  {
    id: "a3", name: "Security", nameHe: "אבטחה", state: "idle",
    task: null, model: "claude-haiku", runs: 3, tier: 1,
    ticketId: null, elapsedSec: 0, tokens: 5210, costUsd: 0.0021,
    tools: ["analyze_ticket", "search_knowledge"],
    recentActions: [
      { label: "השלים בדיקת אבטחה", time: "לפני 4 ש", type: "completed" },
    ],
  },
  {
    id: "a4", name: "Discovery", nameHe: "גילוי", state: "planning",
    task: "סריקת ידע חדש", model: "claude-haiku", runs: 5, tier: 1,
    ticketId: null, elapsedSec: 28, tokens: 2140, costUsd: 0.0009,
    tools: ["search_knowledge", "add_comment"],
    recentActions: [
      { label: "החל סריקת ידע",    time: "לפני 30 ש", type: "search" },
    ],
  },
  {
    id: "a5", name: "Documentation", nameHe: "תיעוד", state: "completed",
    task: "עדכון מאמר ידע", model: "claude-haiku", runs: 12, tier: 1,
    ticketId: null, elapsedSec: 0, tokens: 8870, costUsd: 0.0035,
    tools: ["search_knowledge", "add_comment"],
    recentActions: [
      { label: "עדכן מאמר ידע #44", time: "לפני 12 דק", type: "action"    },
      { label: "ניתח פערי ידע",      time: "לפני 15 דק", type: "analyze"   },
    ],
  },
  {
    id: "a6", name: "Research", nameHe: "מחקר", state: "waiting",
    task: "ממתין לנתוני לקוח", model: "claude-sonnet", runs: 4, tier: 2,
    ticketId: "1039", elapsedSec: 890, tokens: 41600, costUsd: 0.0416,
    tools: ["analyze_ticket", "search_knowledge", "escalate"],
    recentActions: [
      { label: "שלח שאלות ללקוח",  time: "לפני 15 דק", type: "action"  },
      { label: "ניתח קריאה #1039", time: "לפני 16 דק", type: "analyze" },
    ],
  },
];

const MOCK_STATS = { agents: MOCK_AGENTS.length, runs_today: 49, pending_review: 2, avg_confidence: 74 };

// ── Mock delegation edges ─────────────────────────────────────────────────────
// Each edge: { from: agentId, to: agentId, label: string }
const MOCK_DELEGATIONS = [
  { from: "a1", to: "a2", label: "בקשת המלצה לסגירה"  },
  { from: "a4", to: "a5", label: "עדכון תיעוד ידע"      },
  { from: "a2", to: "a6", label: "מחקר לקוח נוסף"      },
];

// ── Mock knowledge accesses ───────────────────────────────────────────────────
const MOCK_KNOWLEDGE_ACCESSES = [
  { id: "k1", title: "הגדרת VPN ל-Cisco ASA",      agentId: "a1", time: "לפני 1 דק"  },
  { id: "k2", title: "מדיניות סגירת קריאות",         agentId: "a2", time: "לפני 5 דק"  },
  { id: "k3", title: "רשימת כלי Tier 2",             agentId: "a6", time: "לפני 14 דק" },
  { id: "k4", title: "תבניות תגובה ללקוח",           agentId: "a1", time: "לפני 15 דק" },
  { id: "k5", title: "נהלי אבטחת Active Directory",  agentId: "a3", time: "לפני 4 ש"   },
  { id: "k6", title: "מאמר: בעיות רשת נפוצות",       agentId: "a4", time: "לפני 30 ש"  },
];

// ── Layout constants ──────────────────────────────────────────────────────────
const SVG_SIZE  = 560;
const C         = SVG_SIZE / 2;
const ORBIT_R   = 200;
const CORE_R    = 56;
const RING_OUTER = 142;
const RING_MID   = 112;
const RING_INNER = 85;
const NODE_R     = 30;

// ── Helpers ───────────────────────────────────────────────────────────────────
function fmt(d) {
  return d.toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function hexPoints(cx, cy, r, startAngle = -30) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 180) * (60 * i + startAngle);
    return `${(cx + r * Math.cos(a)).toFixed(2)},${(cy + r * Math.sin(a)).toFixed(2)}`;
  }).join(" ");
}

function agentPos(index, total, orbitR) {
  const angle = (360 / total) * index - 90;
  const rad = (angle * Math.PI) / 180;
  return { x: C + orbitR * Math.cos(rad), y: C + orbitR * Math.sin(rad) };
}

// CSS clip-paths for each shape
const SHAPE_CLIP = {
  circle:  "circle(50%)",
  hex:     "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
  diamond: "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
  square:  "polygon(8% 8%, 92% 8%, 92% 92%, 8% 92%)",
};

// ── Root component ────────────────────────────────────────────────────────────
export default function CommandCenterPage() {
  const isOnline = useOnlineStatus();

  // ── Live data queries (read-only, poll every 15s) ──────────────────────────
  const agentsQuery = useQuery({
    queryKey: ["cc-agents"],
    queryFn:  agentsApi.list,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
  const liveAgentDefs = agentsQuery.data ?? NO_DATA;

  const runsQuery = useQuery({
    queryKey: ["cc-runs"],
    queryFn:  () => agentRunsApi.listAll({ limit: 100 }),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
  const liveRuns = runsQuery.data ?? NO_DATA;

  const statsQuery = useQuery({
    queryKey: ["cc-stats"],
    queryFn:  () => agentRunsApi.stats(),
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
  const liveStats = statsQuery.data;

  // Source classification per displayed payload
  const agentsSource = classifyQuerySource(agentsQuery, liveAgentDefs.length > 0, isOnline);
  const agentsAreLive = agentsSource === "POLLING" || agentsSource === "CACHED";
  const statsSource  = classifyQuerySource(statsQuery, !!liveStats, isOnline);

  // Evidence-based feed truth for the badges/labels. POLLING requires a successful read while
  // online; a failed refresh over held data is STALE; offline held data is CACHED; LIVE is not
  // reachable here because this page has no push source.
  const agentsTruth = classifySourceTruth(agentsQuery, {
    online: isOnline,
    pollingMs: 15_000,
    hasPayload: liveAgentDefs.length > 0,
  });
  const runsTruth = classifySourceTruth(runsQuery, { online: isOnline, pollingMs: 15_000 });

  // Run status drives live agent state. Without a successful read (loading, failed, denied or
  // no evidence) the state is unknown, never "idle". A held cached list is the last known status;
  // it is not animated as current unless both feeds are actually current.
  const runsUsableForState = [
    SOURCE_TRUTH.CACHED,
    SOURCE_TRUTH.POLLING,
    SOURCE_TRUTH.LIVE,
  ].includes(runsTruth.key);
  const feedCurrent = isCurrentTruth(agentsTruth.key) && isCurrentTruth(runsTruth.key);

  // Map live API run status → Command Center agent state
  const RUN_STATUS_TO_STATE = useMemo(() => ({
    pending:                          "planning",
    analyzing:                        "planning",
    diagnosing:                       "planning",
    executing:                        "working",
    connection_active:                "working",
    awaiting_technician_review:       "approval_required",
    awaiting_approval:                "approval_required",
    waiting_for_customer_connection:  "waiting",
    waiting_for_customer_answer:      "waiting",
    paused:                           "waiting",
    completed:                        "completed",
    resolved:                         "completed",
    approved:                         "completed",
    failed:                           "failed",
    rejected:                         "failed",
  }), []);

  // Derive live agents from API data; fall back to MOCK_AGENTS when empty
  const sourceAgents = useMemo(() => {
    if (!liveAgentDefs.length) return MOCK_AGENTS;
    // Index most recent run per agent
    const runsByAgent = {};
    liveRuns.forEach((run) => {
      if (!runsByAgent[run.agent_id]) runsByAgent[run.agent_id] = run;
    });
    return liveAgentDefs.map((def) => {
      const run      = runsByAgent[def.id];
      const identity = identityFor(def.name);
      const state    = !runsUsableForState
        ? "unknown"
        : run ? (RUN_STATUS_TO_STATE[run.status] ?? "unknown") : "idle";
      return {
        id:            def.id,
        name:          def.name,
        nameHe:        identity.roleHe ?? def.name,
        state,
        task:          run ? (run.task_description ?? (run.ticket_id ? `קריאה #${run.ticket_id}` : null)) : null,
        model:         def.model ?? null,
        runs:          null,
        tier:          tierNumber(def.permission_tier),
        ticketId:      run?.ticket_id ? String(run.ticket_id) : null,
        elapsedSec:    0,
        tools:         def.tools ?? [],
        recentActions: [],
        tokens:        null,
        costUsd:       null,
      };
    });
  }, [liveAgentDefs, liveRuns, runsUsableForState, RUN_STATUS_TO_STATE]);

  // Stats: prefer live; fallback to mock
  // /api/agent-runs/stats without since_days is all-time, so live "runs" is a total, not "today".
  // Fields the API omits are null (rendered "—"), never a mock number presented as live.
  const displayStats = useMemo(() => {
    if (!liveStats) return MOCK_STATS;
    return {
      agents:         agentsAreLive ? sourceAgents.length : null,
      runs_today:     liveStats.total ?? null,
      pending_review: liveStats.pending_review ?? null,
      avg_confidence: liveStats.avg_confidence ?? null,
    };
  }, [liveStats, sourceAgents.length, agentsAreLive]);
  const statsAreLive = !!liveStats;

  const [coreStateKey, setCoreStateKey]   = useState("active");
  // Manual demo overrides only (agent id → state). Overridden agents are flagged `simulated`.
  const [agentStates,  setAgentStates]    = useState({});
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [showLegend, setShowLegend]       = useState(false);
  const [showTimeline, setShowTimeline]   = useState(false);
  const [showKnowledge, setShowKnowledge] = useState(false);
  const [time, setTime]                   = useState(() => fmt(new Date()));

  useEffect(() => {
    const id = setInterval(() => setTime(fmt(new Date())), 1000);
    return () => clearInterval(id);
  }, []);

  const cycleAgentState = (agentId, sourceState) => {
    setAgentStates((prev) => {
      const cur = prev[agentId] ?? sourceState ?? "idle";
      const idx = AGENT_STATE_ORDER.indexOf(cur);
      const next = AGENT_STATE_ORDER[(idx + 1) % AGENT_STATE_ORDER.length];
      return { ...prev, [agentId]: next };
    });
  };

  const cycleCoreState = () => {
    setCoreStateKey((cur) => {
      const idx = CORE_STATE_ORDER.indexOf(cur);
      return CORE_STATE_ORDER[(idx + 1) % CORE_STATE_ORDER.length];
    });
  };

  // Merge state-machine overrides into source agents (live or mock)
  const agents = sourceAgents.map((a) => (
    a.id in agentStates ? { ...a, state: agentStates[a.id], simulated: true } : a
  ));

  // Flatten all recentActions into a global timeline feed (preserve mock order = newest first)
  const timelineFeed = agents.flatMap((a) =>
    (a.recentActions ?? []).map((act) => ({ ...act, agentId: a.id, agentNameHe: a.nameHe, agentName: a.name }))
  );

  // Agents awaiting approval
  // (a demo-cycled state is not a real approval request, so simulated agents are excluded)
  const pendingApprovals = agents.filter((a) => a.state === "approval_required" && !a.simulated);

  // Aggregate telemetry. Live per-agent tokens/cost are not exposed, so live totals come from the
  // all-time aggregate on the stats API; null (not 0) when it reports none.
  let totalTokens = null;
  let totalCost   = null;
  if (!statsAreLive) {
    totalTokens = agents.reduce((s, a) => s + (a.tokens ?? 0), 0);
    totalCost   = agents.reduce((s, a) => s + (a.costUsd ?? 0), 0);
  } else {
    if (liveStats.total_input_tokens != null || liveStats.total_output_tokens != null) {
      totalTokens = (liveStats.total_input_tokens ?? 0) + (liveStats.total_output_tokens ?? 0);
    }
    totalCost = liveStats.total_cost_usd ?? null;
  }
  const dash = (v, f = (x) => x) => (v == null ? "—" : f(v));

  // Delegation edges and knowledge accesses have no API yet: mock samples only while the page
  // shows mock agents; with live agents they'd reference nonexistent ids, so show none.
  const delegations = agentsAreLive ? NO_DATA : MOCK_DELEGATIONS;
  const knowledgeAccesses = agentsAreLive ? NO_DATA : MOCK_KNOWLEDGE_ACCESSES;
  const sampleKind = agentsAreLive ? "UNAVAILABLE" : agentsSource;

  const palette      = CORE_STATE[coreStateKey] ?? CORE_STATE.idle;
  const selectedData = selectedAgent ? agents.find((a) => a.id === selectedAgent) : null;

  return (
    <div
      className="flex flex-col"
      style={{ minHeight: "100vh", background: "linear-gradient(160deg, #080c17 0%, #0f172a 55%, #080c17 100%)" }}
      dir="rtl"
    >
      {/* ── Status bar ─────────────────────────────────────────────────── */}
      <header className="flex items-center justify-between px-6 py-2.5 border-b border-slate-800/80 z-20 relative">
        <div className="flex items-center gap-3">
          <CoreHexIcon size={22} color={palette.color} />
          <span className="text-white font-semibold text-sm tracking-wide">מרכז פיקוד AI</span>
          <span className="text-slate-600 text-xs hidden sm:inline">Prodigy Command Center</span>
        </div>
        <div className="flex items-center gap-5">
          {[
            ["סוכנים", displayStats.agents, agentsSource],
            [statsAreLive ? "ריצות (סה״כ)" : "ריצות", displayStats.runs_today, statsSource],
            ["לסקירה", displayStats.pending_review, statsSource],
          ].map(([k, v, kind]) => (
            <span key={k} className="hidden md:flex items-center gap-1 text-[11px]">
              <span className="text-slate-500">{k}</span>
              <span className="text-white font-semibold font-mono">{dash(v)}</span>
              <SourceBadge kind={kind} />
            </span>
          ))}
          <span className="hidden lg:flex items-center gap-1 text-[11px] border-r border-slate-800 pr-5">
            <span className="text-slate-500">טוקנים</span>
            <span className="text-indigo-300 font-semibold font-mono">{dash(totalTokens, (v) => v.toLocaleString())}</span>
            <SourceBadge kind={statsAreLive && totalTokens == null ? "UNAVAILABLE" : statsSource} />
          </span>
          <span className="hidden lg:flex items-center gap-1 text-[11px]">
            <span className="text-slate-500">עלות</span>
            <span className="text-emerald-400 font-semibold font-mono">{dash(totalCost, (v) => `$${v.toFixed(4)}`)}</span>
            <SourceBadge kind={statsAreLive && totalCost == null ? "UNAVAILABLE" : statsSource} />
          </span>
          <span className="flex items-center gap-1.5 text-xs" data-testid="cc-status">
            <span
              className={`w-1.5 h-1.5 rounded-full inline-block ${isCurrentTruth(agentsTruth.key) ? "bg-sky-400 animate-pulse" : "bg-slate-500"}`}
            />
            <span className="hidden sm:inline text-slate-400">
              {AGENT_TRUTH_TEXT[agentsTruth.key] ?? "מצב המקור לא ידוע"}
            </span>
            <SourceBadge kind={agentsSource} />
            <SourceTruthBadge truth={agentsTruth} testId="cc-source-truth" />
          </span>
          <span className="text-slate-500 text-[11px] font-mono tabular-nums">{time}</span>
          <button
            onClick={() => setShowTimeline((v) => !v)}
            className="text-[10px] border rounded px-2 py-0.5 transition-colors"
            style={{
              color: showTimeline ? "#7c3aed" : "#475569",
              borderColor: showTimeline ? "#7c3aed55" : "#334155",
              background: showTimeline ? "rgba(124,58,237,0.08)" : "transparent",
            }}
            aria-pressed={showTimeline}
            title="ציר זמן פעולות"
          >
            ≡ ציר זמן
          </button>
          <button
            onClick={() => setShowKnowledge((v) => !v)}
            className="text-[10px] border rounded px-2 py-0.5 transition-colors"
            style={{
              color: showKnowledge ? "#0d9488" : "#475569",
              borderColor: showKnowledge ? "#0d948855" : "#334155",
              background: showKnowledge ? "rgba(13,148,136,0.08)" : "transparent",
            }}
            aria-pressed={showKnowledge}
            title="ידע פעיל"
          >
            📚 ידע
          </button>
          <span className="text-slate-700 text-[9px]">{BUILD_VERSION}</span>
        </div>
      </header>

      {!isOnline && (
        <p
          role="status"
          data-testid="cc-offline"
          className="px-6 py-2 text-xs border-b border-amber-900/40 bg-amber-950/40 text-amber-200"
        >
          {liveAgentDefs.length > 0
            ? "אין חיבור לרשת — מוצג מצב מהטעינה האחרונה; הנתונים לא מתרעננים כרגע."
            : "אין חיבור לרשת — לא ניתן לטעון נתוני מערכת כרגע."}
        </p>
      )}

      {/* ── Canvas ─────────────────────────────────────────────────────── */}
      <main className="flex-1 flex items-center justify-center relative overflow-hidden">
        {/* Dot grid */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle, rgba(99,102,241,0.055) 1px, transparent 1px)", backgroundSize: "44px 44px" }}
        />
        {/* Floor glow */}
        <div aria-hidden="true" className="absolute pointer-events-none"
          style={{ bottom: "8%", left: "50%", transform: "translateX(-50%)", width: 700, height: 220,
            background: `radial-gradient(ellipse, ${palette.glow} 0%, transparent 70%)`, filter: "blur(50px)",
            transition: "background 1.2s ease" }}
        />

        {/* Constellation */}
        <div className="relative z-10 flex items-center justify-center"
          style={{
            width: SVG_SIZE,
            height: SVG_SIZE,
            maxWidth: "min(100vw, calc(100vh - 160px))",
            maxHeight: "calc(100vh - 160px)",
            aspectRatio: "1 / 1",
          }}>

          <ConstellationSVG palette={palette} agents={agents} selectedAgent={selectedAgent}
            delegations={delegations} onCoreClick={cycleCoreState} />

          {/* Agent avatar overlays */}
          {agents.map((agent, i) => {
            const { x, y } = agentPos(i, agents.length, ORBIT_R);
            const identity  = identityFor(agent.name);
            const stateInfo = AGENT_STATE[agent.state]   ?? AGENT_STATE.idle;
            const ringColor = STATE_RING_COLOR[agent.state] ?? "transparent";
            // A held or failed feed never animates: motion here would claim work is progressing now.
            const pulsing = stateInfo.pulsing && feedCurrent;
            const isSelected = selectedAgent === agent.id;
            const px = (x / SVG_SIZE) * 100;
            const py = (y / SVG_SIZE) * 100;
            const D  = NODE_R * 2;

            return (
              <button
                key={agent.id}
                aria-label={`${agent.nameHe} — ${stateInfo.text}`}
                onClick={() => setSelectedAgent(isSelected ? null : agent.id)}
                className="absolute flex flex-col items-center gap-0.5 cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent group"
                style={{ left: `${px}%`, top: `${py}%`, transform: "translate(-50%, -50%)" }}
              >
                {/* State ring (outermost) */}
                {stateInfo.ringOpacity > 0 && (
                  <span
                    aria-hidden="true"
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      width: D + 14, height: D + 14,
                      top: "50%", left: "50%",
                      transform: "translate(-50%, -50%) translateY(-10px)",
                      border: `2px solid ${ringColor}`,
                      opacity: stateInfo.ringOpacity,
                      animation: pulsing ? "cc-ring-pulse 1.8s ease-in-out infinite" : "none",
                      boxShadow: pulsing ? `0 0 10px ${ringColor}88` : "none",
                    }}
                  />
                )}

                {/* Selection ring */}
                {isSelected && (
                  <span
                    aria-hidden="true"
                    className="absolute rounded-full pointer-events-none"
                    style={{
                      width: D + 20, height: D + 20,
                      top: "50%", left: "50%",
                      transform: "translate(-50%, -50%) translateY(-10px)",
                      border: `1.5px solid ${palette.color}`,
                      opacity: 0.7,
                    }}
                  />
                )}

                {/* Avatar body */}
                <div
                  className="flex items-center justify-center text-lg transition-transform duration-200 group-hover:scale-110"
                  style={{
                    width: D, height: D,
                    background: isSelected ? identity.bg : identity.dimBg,
                    border: `2px solid ${isSelected ? identity.accent : identity.accent + "88"}`,
                    clipPath: SHAPE_CLIP[identity.shape] ?? SHAPE_CLIP.circle,
                    boxShadow: isSelected
                      ? `0 0 16px ${identity.accent}66, 0 0 32px ${identity.accent}22`
                      : agent.state === "approval_required"
                      ? `0 0 12px ${ringColor}55`
                      : "none",
                    transition: "all 0.3s ease",
                  }}
                  aria-hidden="true"
                >
                  {identity.icon}
                </div>

                {/* Name */}
                <span
                  className="text-[9px] font-semibold whitespace-nowrap mt-0.5"
                  style={{ color: isSelected ? identity.accent : "#94a3b8" }}
                >
                  {agent.nameHe}
                </span>

                {/* State dot + text */}
                <span className="flex items-center gap-0.5 text-[8px] whitespace-nowrap">
                  <span className="w-1 h-1 rounded-full flex-shrink-0 inline-block"
                    style={{ background: stateInfo.dot }} />
                  <span style={{ color: stateInfo.dot }}>{stateInfo.text}</span>
                </span>
              </button>
            );
          })}
        </div>

        {/* Agent detail panel */}
        {selectedData && (
          <AgentPanel agent={selectedData} onClose={() => setSelectedAgent(null)}
            palette={palette} onCycleState={() => cycleAgentState(selectedData.id, selectedData.state)}
            delegations={delegations} sampleKind={sampleKind} isLive={feedCurrent}
            workspaceHref={agentsAreLive && !selectedData.simulated ? `/agents/${selectedData.id}` : null} />
        )}

        {/* Activity timeline */}
        {showTimeline && (
          <ActivityTimeline
            feed={timelineFeed}
            agents={agents}
            sourceKind={sampleKind}
            onClose={() => setShowTimeline(false)}
          />
        )}

        {/* Approvals banner */}
        {pendingApprovals.length > 0 && (
          <ApprovalsPanel approvals={pendingApprovals} sourceKind={agentsSource} />
        )}

        {/* Knowledge panel */}
        {showKnowledge && (
          <KnowledgePanel
            accesses={knowledgeAccesses}
            sourceKind={sampleKind}
            agents={agents}
            onClose={() => setShowKnowledge(false)}
            timelineOpen={showTimeline}
          />
        )}
      </main>

      {/* ── Bottom: Core state selector + legend toggle ────────────────── */}
      <footer className="flex items-center justify-between px-6 py-2 border-t border-slate-800/60 z-20 relative">
        {/* Core state strip */}
        <div className="flex items-center gap-2">
          <span className="text-slate-600 text-[10px] uppercase tracking-wide hidden sm:inline">מצב ליבה:</span>
          <SourceBadge kind="MOCK" />
          <div className="flex gap-1.5">
            {CORE_STATE_ORDER.map((stateKey) => {
              const p = CORE_STATE[stateKey];
              const isActive = coreStateKey === stateKey;
              return (
                <button
                  key={stateKey}
                  onClick={() => setCoreStateKey(stateKey)}
                  className="text-[10px] px-2 py-0.5 rounded-full border font-medium transition-all"
                  style={{
                    borderColor: isActive ? p.color : p.color + "44",
                    background:  isActive ? p.outer + "cc" : "transparent",
                    color:       isActive ? p.color : p.color + "88",
                    boxShadow:   isActive ? `0 0 8px ${p.glow}` : "none",
                  }}
                  aria-pressed={isActive}
                  aria-label={`מצב ליבה: ${p.label}`}
                >
                  {p.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Legend toggle */}
        <button
          onClick={() => setShowLegend((v) => !v)}
          className="text-[10px] text-slate-500 hover:text-slate-300 border border-slate-700/50 rounded px-2 py-0.5 transition-colors"
          aria-expanded={showLegend}
        >
          {showLegend ? "הסתר מקרא" : "מקרא מצבים"}
        </button>
      </footer>

      {/* ── State machine legend overlay ───────────────────────────────── */}
      {showLegend && (
        <div className="absolute bottom-12 left-0 right-0 z-30 flex justify-center pointer-events-none">
          <div className="pointer-events-auto rounded-xl border border-slate-700/50 bg-slate-900/95 backdrop-blur-sm p-4 mx-4"
            style={{ maxWidth: 560 }}>
            <p className="text-xs font-semibold text-slate-400 mb-3 text-center tracking-wide">מכונת מצבים — סוכן</p>
            <div className="grid grid-cols-4 gap-2" dir="rtl">
              {AGENT_STATE_ORDER.map((s) => {
                const st = AGENT_STATE[s];
                return (
                  <div key={s} className="flex items-center gap-1.5 rounded-lg px-2 py-1.5 bg-slate-800/60 border border-slate-700/40">
                    <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: st.dot }} />
                    <span className="text-[10px]" style={{ color: st.label }}>{st.text === "idle" ? "המתנה" : st.text}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-3 pt-3 border-t border-slate-800 text-[10px] text-slate-600 text-center">
              לחץ על סוכן → פאנל פרטים → החלף מצב · לחץ על ליבה לשינוי מצב מרכזי
            </div>
          </div>
        </div>
      )}

      <style>{`
        .cc-core-hitbox { outline: none; }
        .cc-core-hitbox:focus-visible { outline: 2px solid #38bdf8; outline-offset: 2px; }
        @keyframes cc-spin-cw   { from { transform: rotate(0deg);   } to { transform: rotate(360deg);  } }
        @keyframes cc-spin-ccw  { from { transform: rotate(0deg);   } to { transform: rotate(-360deg); } }
        @keyframes cc-pulse-glow {
          0%, 100% { opacity: 0.55; transform: scale(1);    }
          50%      { opacity: 1;    transform: scale(1.06); }
        }
        @keyframes cc-hex-breathe { 0%, 100% { opacity: 0.85; } 50% { opacity: 1; } }
        @keyframes cc-link-flow   { 0% { stroke-dashoffset: 40; } 100% { stroke-dashoffset: 0; } }
        @keyframes cc-ring-pulse  {
          0%, 100% { opacity: 0.5; transform: translate(-50%, -50%) translateY(-10px) scale(1);    }
          50%      { opacity: 1;   transform: translate(-50%, -50%) translateY(-10px) scale(1.08); }
        }
        @keyframes cc-panel-in    { from { opacity: 0; transform: translateX(16px);  } to { opacity: 1; transform: translateX(0); } }
        @keyframes cc-timeline-in { from { opacity: 0; transform: translateX(-16px); } to { opacity: 1; transform: translateX(0); } }
      `}</style>
    </div>
  );
}

// ── ConstellationSVG ──────────────────────────────────────────────────────────
export function ConstellationSVG({ palette, agents, selectedAgent, delegations = [], onCoreClick }) {
  return (
    <svg width={SVG_SIZE} height={SVG_SIZE} viewBox={`0 0 ${SVG_SIZE} ${SVG_SIZE}`}
      style={{ width: "100%", height: "100%", overflow: "visible", position: "absolute", top: 0, left: 0 }}
    >
      {/* Everything below is purely decorative except the Clickable Core overlay, which is
          deliberately placed OUTSIDE this group: aria-hidden="true" on an ancestor excludes the
          whole subtree from the accessibility tree with no override, so the one real interactive
          control in this SVG must not be a descendant of it, or a screen reader would silently
          skip it even though it stays in the Tab order. */}
      <g aria-hidden="true">
      <defs>
        <radialGradient id="cc-core-grad" cx="50%" cy="40%" r="60%">
          <stop offset="0%"  stopColor={palette.color} stopOpacity="0.35" />
          <stop offset="60%" stopColor={palette.ring}  stopOpacity="0.15" />
          <stop offset="100%" stopColor="#0f172a"       stopOpacity="0.9"  />
        </radialGradient>
        <filter id="cc-glow">
          <feGaussianBlur stdDeviation="10" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="cc-glow-sm">
          <feGaussianBlur stdDeviation="5" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* Ambient glow */}
      <circle cx={C} cy={C} r={RING_OUTER + 25}
        fill={palette.glow.replace("0.35", "0.07")}
        style={{ animation: "cc-pulse-glow 4s ease-in-out infinite" }}
      />

      {/* Orbit guide */}
      <circle cx={C} cy={C} r={ORBIT_R} fill="none"
        stroke={palette.ring} strokeWidth="0.5" strokeOpacity="0.1" strokeDasharray="3 9"
      />

      {/* Outer ring CW */}
      <g style={{ transformOrigin: `${C}px ${C}px`, animation: "cc-spin-cw 28s linear infinite" }}>
        <circle cx={C} cy={C} r={RING_OUTER} fill="none"
          stroke={palette.color} strokeWidth="1" strokeOpacity="0.2" strokeDasharray="8 16"
        />
        {[0, 90, 180, 270].map((deg) => {
          const rad = (deg - 90) * Math.PI / 180;
          return <circle key={deg} cx={C + RING_OUTER * Math.cos(rad)} cy={C + RING_OUTER * Math.sin(rad)}
            r="2.5" fill={palette.color} fillOpacity="0.45" />;
        })}
      </g>

      {/* Mid ring CCW */}
      <g style={{ transformOrigin: `${C}px ${C}px`, animation: "cc-spin-ccw 18s linear infinite" }}>
        <circle cx={C} cy={C} r={RING_MID} fill="none"
          stroke={palette.ring} strokeWidth="0.75" strokeOpacity="0.3" strokeDasharray="4 22"
        />
        {[45, 225].map((deg) => {
          const rad = (deg - 90) * Math.PI / 180;
          return <circle key={deg} cx={C + RING_MID * Math.cos(rad)} cy={C + RING_MID * Math.sin(rad)}
            r="3" fill={palette.color} fillOpacity="0.7" filter="url(#cc-glow-sm)" />;
        })}
      </g>

      {/* Inner ring static */}
      <circle cx={C} cy={C} r={RING_INNER} fill="none"
        stroke={palette.ring} strokeWidth="0.5" strokeOpacity="0.18"
      />

      {/* Connection lines */}
      {agents.map((agent, i) => {
        const { x, y } = agentPos(i, agents.length, ORBIT_R);
        const identity  = identityFor(agent.name);
        const isActive  = ["working", "planning", "approval_required"].includes(agent.state);
        const isSelected = selectedAgent === agent.id;
        const dx = x - C, dy = y - C, dist = Math.sqrt(dx * dx + dy * dy);
        const ex = C + (CORE_R * dx) / dist,   ey = C + (CORE_R * dy) / dist;
        const nx = x - (NODE_R * dx) / dist,   ny = y - (NODE_R * dy) / dist;
        return (
          <line key={agent.id} x1={ex} y1={ey} x2={nx} y2={ny}
            stroke={isSelected ? identity.accent : isActive ? identity.accent : "#334155"}
            strokeWidth={isSelected ? 1.5 : isActive ? 0.75 : 0.5}
            strokeOpacity={isSelected ? 0.9 : isActive ? 0.5 : 0.2}
            strokeDasharray={isActive ? "6 6" : undefined}
            style={isActive ? { animation: "cc-link-flow 1.5s linear infinite" } : undefined}
          />
        );
      })}

      {/* Delegation lines — agent-to-agent curved paths */}
      {delegations.map((edge, i) => {
        const fromIdx = agents.findIndex((a) => a.id === edge.from);
        const toIdx   = agents.findIndex((a) => a.id === edge.to);
        if (fromIdx === -1 || toIdx === -1) return null;
        const from = agentPos(fromIdx, agents.length, ORBIT_R);
        const to   = agentPos(toIdx,   agents.length, ORBIT_R);
        // Offset the control point toward the center to make a curved arc
        const mx = (from.x + to.x) / 2;
        const my = (from.y + to.y) / 2;
        // Pull control point 30% toward center
        const cx_ = mx + (C - mx) * 0.3;
        const cy_ = my + (C - my) * 0.3;
        const d = `M${from.x.toFixed(1)},${from.y.toFixed(1)} Q${cx_.toFixed(1)},${cy_.toFixed(1)} ${to.x.toFixed(1)},${to.y.toFixed(1)}`;
        const fromAgent = agents[fromIdx];
        const identity  = identityFor(fromAgent.name);
        return (
          <path key={i} d={d}
            fill="none"
            stroke={identity.accent}
            strokeWidth="1"
            strokeOpacity="0.35"
            strokeDasharray="4 8"
            style={{ animation: "cc-link-flow 2.5s linear infinite" }}
          />
        );
      })}

      {/* Core Orb */}
      <polygon points={hexPoints(C, C, CORE_R + 9)}
        fill={palette.glow.replace("0.35", "0.12")} stroke="none"
        filter="url(#cc-glow)"
        style={{ animation: "cc-pulse-glow 3s ease-in-out infinite" }}
      />
      <polygon points={hexPoints(C, C, CORE_R)}
        fill="url(#cc-core-grad)" stroke={palette.color} strokeWidth="1.5" strokeOpacity="0.7"
        filter="url(#cc-glow-sm)"
        style={{ animation: "cc-hex-breathe 3s ease-in-out infinite" }}
      />
      <polygon points={hexPoints(C, C, CORE_R - 8)}
        fill="none" stroke={palette.ring} strokeWidth="0.75" strokeOpacity="0.4"
      />
      <text x={C} y={C - 5} textAnchor="middle" fill={palette.color}
        fontSize="11" fontWeight="700" fontFamily="monospace" letterSpacing="3"
        style={{ animation: "cc-hex-breathe 3s ease-in-out infinite" }}
      >PRODIGY</text>
      <text x={C} y={C + 9} textAnchor="middle" fill="#64748b"
        fontSize="8" fontFamily="monospace" letterSpacing="2"
      >CORE</text>
      </g>

      {/* Clickable Core overlay -- deliberately a sibling of the decorative <g> above, not a
          descendant of it, so it stays reachable in the accessibility tree. */}
      {onCoreClick && (
        <circle cx={C} cy={C} r={CORE_R + 2} fill="transparent"
          className="cc-core-hitbox"
          style={{ cursor: "pointer" }}
          onClick={onCoreClick}
          onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && onCoreClick()}
          role="button"
          tabIndex={0}
          aria-label="שנה מצב ליבה — לחץ Enter או רווח"
        />
      )}
    </svg>
  );
}

// ── KnowledgePanel ────────────────────────────────────────────────────────────
function KnowledgePanel({ accesses, sourceKind, agents, onClose, timelineOpen }) {
  return (
    <div
      className="absolute z-30 rounded-xl border backdrop-blur-sm overflow-hidden flex flex-col"
      style={{
        top: 16,
        // Sits left of the timeline on wide screens; on a phone the offset is clamped
        // so the panel stays inside the viewport instead of being pushed off the left edge.
        right: timelineOpen ? "min(308px, max(16px, calc(100% - 256px)))" : 16,
        width: 240,
        maxWidth: "calc(100% - 32px)",
        maxHeight: "calc(100% - 32px)",
        background: "rgba(10,15,26,0.96)",
        borderColor: "#0d948855",
        boxShadow: "0 0 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(13,148,136,0.08)",
        animation: "cc-timeline-in 0.2s ease-out",
        transition: "right 0.2s ease",
      }}
      role="complementary"
      aria-label="ידע פעיל"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b flex-shrink-0"
        style={{ borderColor: "#0d948830" }}>
        <span className="text-xs font-semibold text-teal-300 flex items-center gap-1.5">ידע פעיל <SourceBadge kind={sourceKind} /></span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-xl leading-none w-6 text-center"
          aria-label="סגור">×</button>
      </div>

      {/* Access list */}
      <div className="overflow-y-auto flex-1 py-1" style={{ scrollbarWidth: "none" }}>
        {accesses.length === 0 && (
          <p className="text-xs text-slate-600 italic px-4 py-3 text-center">אין מקור נתונים לגישות ידע</p>
        )}
        {accesses.map((entry) => {
          const agent    = agents.find((a) => a.id === entry.agentId);
          const identity = agent ? (identityFor(agent.name)) : null;
          return (
            <div key={entry.id} className="flex items-start gap-2.5 px-4 py-2 border-b border-slate-800/40 last:border-0">
              {/* Agent accent dot */}
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5"
                style={{ background: identity?.accent ?? "#475569" }} />
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-300 leading-snug">{entry.title}</p>
                <div className="flex items-center gap-1.5 mt-0.5">
                  {identity && (
                    <span className="text-[9px]" style={{ color: identity.accent }}>
                      {identity.icon} {agent.nameHe}
                    </span>
                  )}
                  <span className="text-[9px] text-slate-600">{entry.time}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-slate-800/60 px-4 py-1.5 text-[9px] text-slate-600 text-center"
        style={{ borderColor: "#0d948820" }}>
        {accesses.length} מאמרים{accesses.length > 0 && " · נתוני הדגמה"}
      </div>
    </div>
  );
}

// ── AgentPanel ────────────────────────────────────────────────────────────────
const ACTION_ICONS = { analyze: "🔍", search: "📚", action: "⚡", completed: "✅" };

function fmtElapsed(sec) {
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function AgentPanel({ agent, onClose, palette, onCycleState, delegations = [], sampleKind, isLive = false, workspaceHref = null }) {
  const identity  = identityFor(agent.name);
  const stateInfo = AGENT_STATE[agent.state]   ?? AGENT_STATE.idle;
  const ringColor = STATE_RING_COLOR[agent.state] ?? "transparent";

  const isRunning      = ["working", "planning", "waiting"].includes(agent.state);
  const outEdges       = delegations.filter((e) => e.from === agent.id);
  const inEdges        = delegations.filter((e) => e.to   === agent.id);
  const [elapsed, setElapsed] = useState(agent.elapsedSec ?? 0);
  useEffect(() => {
    setElapsed(agent.elapsedSec ?? 0);
    // The ticker only advances on a current feed. Cached/stale data must not keep counting upward
    // as if the run were still progressing.
    if (!isRunning || !isLive) return;
    const id = setInterval(() => setElapsed((v) => v + 1), 1000);
    return () => clearInterval(id);
  }, [agent.id, agent.state, agent.elapsedSec, isRunning, isLive]);

  return (
    <div
      className="absolute top-4 left-4 z-30 rounded-xl border backdrop-blur-sm overflow-hidden flex flex-col"
      style={{
        width: 284,
        maxWidth: "calc(100% - 32px)",
        maxHeight: "calc(100% - 32px)",
        background: "rgba(10,15,26,0.96)",
        borderColor: identity.accent + "55",
        boxShadow: `0 0 30px rgba(0,0,0,0.7), 0 0 0 1px ${identity.accent}22`,
        animation: "cc-panel-in 0.2s ease-out",
      }}
      role="dialog"
      aria-label={`פרטי סוכן ${agent.nameHe}`}
    >
      {/* Panel header */}
      <div className="flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: identity.accent + "33",
          background: `linear-gradient(90deg, ${identity.bg} 0%, transparent 100%)` }}>
        <div className="flex items-center gap-2.5">
          <div className="flex items-center justify-center text-xl flex-shrink-0"
            style={{
              width: 40, height: 40,
              background: identity.bg,
              border: `2px solid ${identity.accent}`,
              clipPath: SHAPE_CLIP[identity.shape] ?? SHAPE_CLIP.circle,
            }}
          >{identity.icon}</div>
          <div>
            <p className="text-white text-sm font-semibold leading-none">{agent.nameHe}</p>
            <p className="text-slate-500 text-[10px] mt-0.5">{agent.name}</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-xl leading-none w-6 text-center"
          aria-label="סגור">×</button>
      </div>

      {/* Body */}
      <div className="px-4 py-3 space-y-3 overflow-y-auto flex-1" style={{ scrollbarWidth: "thin" }}>
        {/* State + tier row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full flex-shrink-0"
              style={{ background: stateInfo.dot,
                boxShadow: stateInfo.pulsing ? `0 0 6px ${ringColor}` : "none" }} />
            <span className="text-xs font-medium" style={{ color: stateInfo.label }}>{stateInfo.text}</span>
            {agent.simulated && <SourceBadge kind="MOCK" />}
          </div>
          <div className="flex items-center gap-1.5">
            {elapsed > 0 && (
              <span className="text-[10px] font-mono tabular-nums" style={{ color: identity.accent + "99" }}>
                {fmtElapsed(elapsed)}
              </span>
            )}
            <span className="text-[9px] px-1.5 py-0.5 rounded border"
              style={{ color: identity.accent, borderColor: identity.accent + "44", background: identity.bg }}>
              Tier {agent.tier ?? "—"}
            </span>
          </div>
        </div>

        {/* Current task */}
        {agent.task ? (
          <div className="rounded-lg px-3 py-2" style={{ background: identity.dimBg, border: `1px solid ${identity.accent}22` }}>
            <p className="text-[10px] uppercase tracking-wide mb-1" style={{ color: identity.accent + "aa" }}>משימה נוכחית</p>
            <p className="text-xs text-slate-300">{agent.task}</p>
          </div>
        ) : (
          <div className="rounded-lg px-3 py-2 bg-slate-900/50 border border-slate-800/50">
            <p className="text-xs text-slate-600 italic">אין משימה פעילה</p>
          </div>
        )}

        {/* Tools */}
        {agent.tools?.length > 0 && (
          <div>
            <p className="text-[9px] text-slate-600 uppercase tracking-wide mb-1.5">כלים</p>
            <div className="flex flex-wrap gap-1">
              {agent.tools.map((tool) => (
                <span key={tool}
                  className="text-[9px] px-1.5 py-0.5 rounded border leading-tight"
                  style={{ color: identity.accent + "cc", borderColor: identity.accent + "33", background: identity.dimBg }}>
                  {tool}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Delegation edges */}
        {(outEdges.length > 0 || inEdges.length > 0) && (
          <div>
            <p className="text-[9px] text-slate-600 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">האצלות <SourceBadge kind={sampleKind} /></p>
            <div className="space-y-1">
              {outEdges.map((e, i) => (
                <div key={`out-${i}`} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-slate-500 flex-shrink-0">↗</span>
                  <span className="text-slate-400 truncate">{e.label}</span>
                </div>
              ))}
              {inEdges.map((e, i) => (
                <div key={`in-${i}`} className="flex items-center gap-1.5 text-[10px]">
                  <span className="text-slate-500 flex-shrink-0">↘</span>
                  <span className="text-slate-400 truncate">{e.label}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent actions */}
        {agent.recentActions?.length > 0 && (
          <div>
            <p className="text-[9px] text-slate-600 uppercase tracking-wide mb-1.5 flex items-center gap-1.5">פעולות אחרונות <SourceBadge kind={sampleKind} /></p>
            <div className="space-y-1">
              {agent.recentActions.map((act, i) => (
                <div key={i} className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className="text-[10px] flex-shrink-0" aria-hidden="true">{ACTION_ICONS[act.type] ?? "▸"}</span>
                    <span className="text-[10px] text-slate-400 truncate">{act.label}</span>
                  </div>
                  <span className="text-[9px] text-slate-600 flex-shrink-0 whitespace-nowrap">{act.time}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-2 gap-2 pt-1 border-t border-slate-800/50">
          <StatCell label="מודל"    value={agent.model ?? "—"}  accent={identity.accent} />
          <StatCell label="ריצות"   value={agent.runs == null ? "—" : String(agent.runs)} accent={identity.accent} />
          {agent.tokens != null && (
            <StatCell label="טוקנים" value={agent.tokens.toLocaleString()} accent={identity.accent} />
          )}
          {agent.costUsd != null && (
            <StatCell label="עלות"   value={`$${agent.costUsd.toFixed(4)}`} accent={identity.accent} />
          )}
        </div>

        {/* Ticket link */}
        {agent.ticketId && (
          <Link
            to={`/tickets/${agent.ticketId}`}
            className="flex items-center justify-between w-full text-[10px] py-1.5 px-3 rounded-lg border transition-colors"
            style={{ color: identity.accent, borderColor: identity.accent + "44", background: identity.dimBg }}
          >
            <span>פתח קריאה #{agent.ticketId}</span>
            <span aria-hidden="true">←</span>
          </Link>
        )}

        {/* Employee workspace link — live roster agents only; mock/demo ids have no real page */}
        {workspaceHref && (
          <Link
            to={workspaceHref}
            className="flex items-center justify-between w-full text-[10px] py-1.5 px-3 rounded-lg border transition-colors"
            style={{ color: identity.accent, borderColor: identity.accent + "44", background: identity.dimBg }}
          >
            <span>פתח עובד בסביבת העבודה</span>
            <span aria-hidden="true">←</span>
          </Link>
        )}

        {/* Approval CTA */}
        {agent.state === "approval_required" && (
          <div className="rounded-lg border border-amber-700/60 bg-amber-950/50 px-3 py-2.5 text-center space-y-0.5">
            <p className="text-amber-300 text-xs font-bold">⚠ ממתין לאישורך</p>
            <p className="text-amber-700 text-[10px]">פתח את כרטיס הקריאה לאישור הפעולה</p>
          </div>
        )}

        {/* State cycle (demo/mock) */}
        <button
          onClick={onCycleState}
          className="w-full text-[10px] py-1.5 rounded-lg border border-slate-700/50 text-slate-500 hover:text-slate-300 hover:border-slate-600 transition-colors text-center"
        >
          ← החלף מצב (הדגמה — אינו משפיע על המערכת)
        </button>
      </div>
    </div>
  );
}

// ── ActivityTimeline ──────────────────────────────────────────────────────────
function ActivityTimeline({ feed, sourceKind, agents, onClose }) {
  return (
    <div
      className="absolute top-4 right-4 z-30 rounded-xl border backdrop-blur-sm overflow-hidden flex flex-col"
      style={{
        width: 256,
        maxWidth: "calc(100% - 32px)",
        maxHeight: "calc(100% - 32px)",
        background: "rgba(10,15,26,0.96)",
        borderColor: "#334155",
        boxShadow: "0 0 30px rgba(0,0,0,0.6), 0 0 0 1px rgba(99,102,241,0.1)",
        animation: "cc-timeline-in 0.2s ease-out",
      }}
      role="complementary"
      aria-label="ציר זמן פעולות"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-800/80 flex-shrink-0">
        <span className="text-xs font-semibold text-slate-300 flex items-center gap-1.5">ציר זמן פעולות <SourceBadge kind={sourceKind} /></span>
        <button onClick={onClose} className="text-slate-500 hover:text-slate-200 text-xl leading-none w-6 text-center"
          aria-label="סגור">×</button>
      </div>

      {/* Feed */}
      <div className="overflow-y-auto flex-1 py-1" style={{ scrollbarWidth: "none" }}>
        {feed.length === 0 ? (
          <p className="text-xs text-slate-600 italic px-4 py-3 text-center">אין מקור נתונים לפעולות אחרונות</p>
        ) : (
          feed.map((entry, i) => {
            const identity = identityFor(entry.agentName);
            return (
              <div key={i} className="flex items-start gap-2.5 px-4 py-2 border-b border-slate-800/40 last:border-0">
                {/* Vertical line + dot */}
                <div className="flex flex-col items-center flex-shrink-0 mt-0.5" style={{ width: 10 }}>
                  <span className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: identity.accent, boxShadow: `0 0 4px ${identity.accent}66` }} />
                  {i < feed.length - 1 && (
                    <span className="w-px flex-1 mt-0.5" style={{ background: identity.accent + "22", minHeight: 14 }} />
                  )}
                </div>

                <div className="flex-1 min-w-0">
                  {/* Agent + type icon */}
                  <div className="flex items-center justify-between gap-1 mb-0.5">
                    <span className="text-[10px] font-semibold truncate" style={{ color: identity.accent }}>
                      {identity.icon} {entry.agentNameHe}
                    </span>
                    <span className="text-[9px] flex-shrink-0" aria-hidden="true">{ACTION_ICONS[entry.type] ?? "▸"}</span>
                  </div>
                  {/* Action label */}
                  <p className="text-[10px] text-slate-300 leading-snug truncate">{entry.label}</p>
                  {/* Time */}
                  <p className="text-[9px] text-slate-600 mt-0.5">{entry.time}</p>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer count */}
      <div className="flex-shrink-0 border-t border-slate-800/60 px-4 py-1.5 text-[9px] text-slate-600 text-center">
        {feed.length} פעולות{feed.length > 0 && " · נתוני הדגמה"}
      </div>
    </div>
  );
}

// ── ApprovalsPanel ────────────────────────────────────────────────────────────
function ApprovalsPanel({ approvals, sourceKind }) {
  return (
    <div
      className="absolute bottom-4 left-1/2 z-30 rounded-xl border backdrop-blur-sm overflow-hidden"
      style={{
        transform: "translateX(-50%)",
        maxWidth: 520,
        width: "calc(100% - 32px)",
        background: "rgba(10,15,26,0.96)",
        borderColor: "#d97706aa",
        boxShadow: "0 0 0 1px rgba(217,119,6,0.15), 0 0 40px rgba(217,119,6,0.15)",
        animation: "cc-panel-in 0.25s ease-out",
      }}
      role="alert"
      aria-live="polite"
      aria-label="פעולות הממתינות לאישור"
    >
      {/* Header strip */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-amber-900/40 bg-amber-950/40">
        <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse flex-shrink-0" aria-hidden="true" />
        <span className="text-amber-300 text-xs font-bold tracking-wide">
          ממתין לאישורך — {approvals.length} {approvals.length === 1 ? "פעולה" : "פעולות"}
        </span>
        <span className="text-amber-700 text-[10px] mr-auto flex items-center gap-1.5">
          <SourceBadge kind={sourceKind} />
          Phase 1 · לצפייה בלבד
        </span>
      </div>

      {/* Approval rows */}
      <div className="divide-y divide-amber-900/20">
        {approvals.map((agent) => {
          const identity = identityFor(agent.name);
          return (
            <div key={agent.id} className="flex items-center gap-3 px-4 py-2.5">
              {/* Agent avatar mini */}
              <div className="flex items-center justify-center text-base flex-shrink-0"
                style={{
                  width: 32, height: 32,
                  background: identity.bg,
                  border: `2px solid ${identity.accent}`,
                  clipPath: SHAPE_CLIP[identity.shape] ?? SHAPE_CLIP.circle,
                }}>
                {identity.icon}
              </div>

              {/* Agent info */}
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold truncate" style={{ color: identity.accent }}>
                  {agent.nameHe}
                </p>
                {agent.task && (
                  <p className="text-[10px] text-slate-400 truncate">{agent.task}</p>
                )}
              </div>

              {/* Ticket link */}
              {agent.ticketId ? (
                <Link
                  to={`/tickets/${agent.ticketId}`}
                  className="flex-shrink-0 text-[10px] px-2.5 py-1 rounded-lg border transition-colors"
                  style={{ color: "#fbbf24", borderColor: "#d9770644", background: "rgba(120,53,15,0.4)" }}
                >
                  פתח #{agent.ticketId} ←
                </Link>
              ) : (
                <span className="flex-shrink-0 text-[9px] text-amber-800 border border-amber-900/30 rounded px-2 py-0.5">
                  אין קריאה
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatCell({ label, value, accent }) {
  return (
    <div>
      <p className="text-[9px] text-slate-600 uppercase tracking-wide">{label}</p>
      <p className="text-[11px] font-mono mt-0.5 truncate" style={{ color: accent + "cc" }}>{value}</p>
    </div>
  );
}

// ── CoreHexIcon ───────────────────────────────────────────────────────────────
function CoreHexIcon({ size = 20, color = "#7c3aed" }) {
  const c = size / 2;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
      <polygon points={hexPoints(c, c, c - 1)} fill="none" stroke={color} strokeWidth="1.5" />
    </svg>
  );
}
